import { BulkOperationProps } from '@type/bulk-operations';
import { useTranslation } from 'next-i18next';
import {
  useSwrBulkUnlock,
  useSwrBulkRequestInterviews,
} from '@hooks/useSwrApi/endpoints/bulk-operations';
import { useSwrGenerateInterviewQuestions, useSwrSaveInterviewQuestions } from '@hooks/useSwrApi/endpoints/interview';
import {
  Button, Modal, bjToast, TailwindSpinner,
} from '@components/common';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { i18nConfig } from 'i18n-config';
import {
  useState, useCallback, useRef,
} from 'react';
import { PostBulkBaseObject } from '@type/v1-api-types';
import { InterviewQuestionsModal } from '@components/features/modals/interview-questions-modal';
import { fetcher } from '@utils/data-fetching';
import { useSwrJobBasicInfo } from '@hooks/useSwrApi/endpoints/talent-search';
import { useOpenBulkModal } from './hooks/useOpenBulkModal';


interface BulkValidateProps extends BulkOperationProps {
  getLockedApplicantIds?: () => string[];
  hideTriggerIcon?: boolean;
  triggerDisabled?: boolean;
}

/**
 * @description Validate (bulk interview request) selected applicants for a job.
 * If any selected applicants have locked CVs, shows a pricing confirmation modal first,
 * then bulk-unlocks them before requesting interviews.
 * Also handles interview questions generation/saving flow if needed.
 *
 * - **getSelectedApplicants**: Get the selected applicants. Function that returns an array of selected applicants.
 * - **selectAll**: Boolean value that indicates if all applicants are selected.
 * - **status**: Status of the applicants.
 * - **jobSlug**: The job slug.
 * - **locale**: The locale.
 * - **queryParams**: The filters query params object.
 * - **getLockedApplicantIds**: Optional function returning the slugs of locked selected applicants.
 */
export const BulkValidate = (props: BulkValidateProps) => {
  const {
    getSelectedApplicants,
    jobSlug = '',
    locale = i18nConfig.defaultLocale,
    selectAll,
    status,
    queryParams,
    selectByColumn,
    getLockedApplicantIds,
    normalizeSelectedApplicantsForBulkAction,
    hideTriggerIcon = false,
    triggerDisabled = false,
  } = props;

  const { t } = useTranslation('common');

  // Interview questions state
  const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState<string[]>([]);

  // Pricing confirmation modal state
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [lockedCount, setLockedCount] = useState(0);
  const [totalPrice, setTotalPrice] = useState<number | undefined>(undefined);
  const [totalFullPrice, setTotalFullPrice] = useState<number | undefined>(undefined);
  const [isFetchingPrices, setIsFetchingPrices] = useState(false);

  // Keep the post object in a ref so it's available across async steps
  const postObjectRef = useRef<PostBulkBaseObject | undefined>(undefined);
  // Keep the locked slugs in a ref so they're available when the pricing modal is confirmed
  const lockedSlugsRef = useRef<string[]>([]);
  // Keep the selected slugs in a ref so they're available for the interview request
  const selectedSlugsRef = useRef<string[]>([]);

  // Handle the bulk applicants and base post object build
  const { getPostObject } = useOpenBulkModal({
    getSelectedApplicants, selectAll, status, queryParams, selectByColumn, normalizeSelectedApplicantsForBulkAction,
  });

  // SWR: Get job basic info to resolve jobId from jobSlug
  const { data: jobBasicInfo } = useSwrJobBasicInfo(jobSlug, locale);
  const jobId = jobBasicInfo?.jobId;

  // SWR: Bulk request interviews
  const { trigger: triggerBulkRequestInterviews, isMutating: isRequestingInterviews } = useSwrBulkRequestInterviews({
    jobSlug,
    locale,
    onSuccess: () => {
      bjToast.success(t('cv.action.bulk.validate.success'));
    },
    onError: () => {},
  });

  // SWR: Save interview questions
  const { trigger: triggerSaveQuestions, isMutating: isSavingQuestions } = useSwrSaveInterviewQuestions({
    locale,
    jobSlug,
    onSuccess: () => {
      setIsQuestionsModalOpen(false);
      void triggerBulkRequestInterviews({ ids: selectedSlugsRef.current });
    },
  });

  // SWR: Generate interview questions
  const { trigger: triggerGenerateQuestions, isMutating: isGeneratingQuestions } = useSwrGenerateInterviewQuestions({
    locale,
    jobId: jobId ?? 0,
    onSuccess: (response) => {
      setGeneratedQuestions(response.questions);
      setIsQuestionsModalOpen(true);
    },
    onError: () => {},
  });

  // Proceed to interview request (after unlock if needed)
  // Only generate/save questions if the job doesn't have questions saved yet (miniInterview === false/undefined)
  const proceedToInterviewRequest = useCallback(() => {
    if (jobId && !jobBasicInfo?.miniInterview) {
      void triggerGenerateQuestions({});
    } else {
      void triggerBulkRequestInterviews({ ids: selectedSlugsRef.current });
    }
  }, [jobId, jobBasicInfo?.miniInterview, triggerGenerateQuestions, triggerBulkRequestInterviews]);

  // SWR: Bulk unlock
  const { trigger: triggerBulkUnlock, isMutating: isUnlocking } = useSwrBulkUnlock({
    jobSlug,
    locale,
    onSuccess: () => {
      proceedToInterviewRequest();
    },
    onError: () => {},
  });

  // Handle save questions
  const handleSaveQuestions = useCallback((questions: string[]) => {
    void triggerSaveQuestions({ questions });
  }, [triggerSaveQuestions]);

  // Handle close questions modal
  const handleCloseQuestionsModal = useCallback(() => {
    setIsQuestionsModalOpen(false);
    setGeneratedQuestions([]);
  }, []);

  // Handle pricing modal confirm
  const handlePricingConfirm = useCallback(() => {
    setIsPricingModalOpen(false);
    void triggerBulkUnlock({ ids: lockedSlugsRef.current });
  }, [triggerBulkUnlock]);

  // Handle pricing modal cancel
  const handlePricingCancel = useCallback(() => {
    setIsPricingModalOpen(false);
  }, []);

  // Handle bulk validate click
  const handleBulkValidate = async () => {
    const postObject = getPostObject();
    if (!postObject) return;

    bjToast(t('validate.success.message'));

    postObjectRef.current = postObject;

    const lockedSlugs = getLockedApplicantIds ? getLockedApplicantIds() : [];
    lockedSlugsRef.current = lockedSlugs;
    selectedSlugsRef.current = postObject.slugs ?? [];

    if (lockedSlugs.length > 0) {
      setLockedCount(lockedSlugs.length);
      setIsFetchingPrices(true);
      setIsPricingModalOpen(true);

      // Fetch prices for all locked CVs in parallel
      const priceResults = await Promise.all(
        lockedSlugs.map((slug) => fetcher<{ price?: number; fullPrice?: number }>(
          `/v1/resume-operation/${slug}/price`,
          locale,
          { method: 'GET' },
        ).catch(() => ({ price: 0, fullPrice: 0 } as { price?: number; fullPrice?: number }))),
      ) as { price?: number; fullPrice?: number }[];

      const sumPrice = priceResults.reduce((acc, r) => acc + (r.price ?? 0), 0);
      const sumFullPrice = priceResults.reduce((acc, r) => acc + (r.fullPrice ?? r.price ?? 0), 0);

      setTotalPrice(sumPrice);
      setTotalFullPrice(sumFullPrice);
      setIsFetchingPrices(false);
      return;
    }

    proceedToInterviewRequest();
  };

  const isBusy = isUnlocking || isRequestingInterviews || isGeneratingQuestions;
  const isPromoPrice = totalPrice !== undefined && totalFullPrice !== undefined && totalPrice < totalFullPrice;


  // Render component
  // *********************************
  return (
    <>
      {/* Pricing confirmation modal */}
      <Modal
        isOpen={isPricingModalOpen}
        onClose={handlePricingCancel}
        size="sm"
        centered
      >
        <div className="border-b px-4 py-2 text-left">
          <span className="block text-xl font-bold">
            {t('cv.action.bulk.validate.unlock_title')}
          </span>
          <span className="text-sm text-ink-medium">{t('global.label.unlock.price.candidate.agree')}</span>
        </div>
        <div className="p-5">
          <div className="mb-5 text-center">
            {isFetchingPrices ? (
              <div className="flex items-center justify-center py-4">
                <TailwindSpinner className="size-5" />
              </div>
            ) : (
              <div className="w-full justify-center border-b py-3 pt-4 text-sm text-ink-medium">
                {t('cv.action.bulk.validate.unlock_description', { count: lockedCount })}
                {totalPrice !== undefined && (
                  <div className="mt-2 flex items-end justify-center gap-1">
                    {`${t('cv.detail.contact.label')}:`}
                    {isPromoPrice && (
                      <span className="text-sm text-danger line-through">
                        {totalFullPrice}
                      </span>
                    )}
                    <span className="text-[30px] font-bold leading-none text-secondary">
                      {totalPrice}
                    </span>
                    <span className="text-base font-medium text-secondary">
                      {t('global.label.credits')}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="text-center">
            <Button
              role="presentation"
              size="sm"
              color="error"
              onClick={handlePricingCancel}
              className="mr-2"
              disabled={isFetchingPrices}
            >
              {t('global.label.unlock.candidate.deny')}
            </Button>
            <Button
              role="presentation"
              size="sm"
              color="secondary"
              onClick={handlePricingConfirm}
              disabled={isFetchingPrices}
            >
              {t('global.label.unlock.candidate.info')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Interview questions modal */}
      <InterviewQuestionsModal
        isOpen={isQuestionsModalOpen}
        onClose={handleCloseQuestionsModal}
        questions={generatedQuestions}
        onSave={handleSaveQuestions}
        isSaving={isSavingQuestions}
      />

      <Button
        styling="text"
        size="sm"
        color="ink"
        className="grow px-4"
        aria-label={t('cv.action.bulk.validate')}
        isLoading={isBusy}
        disabled={triggerDisabled || isBusy}
        onClick={() => {
          void handleBulkValidate();
        }}
      >
        {!isBusy && !hideTriggerIcon && <CheckCircleIcon className="mr-2 size-5 md:mr-1" />}
        <span className={hideTriggerIcon ? undefined : 'lg:pl-px'}>{t('cv.action.bulk.validate')}</span>
      </Button>
    </>
  );
};
